// SOLVENDA brand palette.
// Aligned with the product UI: warm gold/amber identity, green reserved for
// payment / "Connect" actions. Dark-first, with a matching light mode.

export const brand = {
  gold: {
    50: '#FBF3DD',
    100: '#F6E6BC',
    200: '#F0D58C',
    300: '#EAC75F',
    400: '#E5B73B',
    500: '#E0A82E', // primary brand gold
    600: '#C28E1E',
    700: '#9C7016',
    800: '#6E4F11',
    900: '#3F2D0A',
  },
  green: {
    400: '#34C77B',
    500: '#22A06B', // payments / connect accent
    600: '#178A57',
  },
};

// Per-module accent colors (kept in sync with src/data/modules.js).
export const moduleColors = {
  core: '#E0A82E',
  radius: '#7C6CF0',
  fiscal: '#E5A50A',
  olt: '#2DB6A8',
  dns: '#4F9DF7',
  logs: '#22B8CF',
  connect: '#22A06B',
  full: '#E5484D',
};

export const themeSettings = (mode) => {
  const isDark = mode === 'dark';

  return {
    palette: {
      mode,
      primary: {
        main: brand.gold[500],
        light: brand.gold[400],
        dark: brand.gold[700],
        contrastText: '#15110A',
      },
      secondary: {
        main: brand.green[500],
        light: brand.green[400],
        dark: brand.green[600],
        contrastText: '#FFFFFF',
      },
      ...(isDark
        ? {
            background: {
              default: '#0B0E14',
              paper: '#141A23',
              light: '#1A212C',
            },
            text: {
              primary: '#F5F7FA',
              secondary: '#98A6B8',
            },
            divider: 'rgba(255,255,255,0.08)',
          }
        : {
            background: {
              default: '#F6F8FB',
              paper: '#FFFFFF',
              light: '#EEF1F6',
            },
            text: {
              primary: '#10151D',
              secondary: '#55626F',
            },
            divider: 'rgba(16,21,29,0.10)',
          }),
      success: { main: brand.green[500] },
      warning: { main: brand.gold[500] },
      error: { main: '#E5484D' },
      info: { main: '#3B82F6' },
    },
    shape: {
      borderRadius: 12,
    },
    typography: {
      fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
      h1: { fontWeight: 800, fontSize: '3rem', lineHeight: 1.1, letterSpacing: '-0.02em' },
      h2: { fontWeight: 800, fontSize: '2.25rem', lineHeight: 1.15, letterSpacing: '-0.01em' },
      h3: { fontWeight: 700, fontSize: '1.6rem', lineHeight: 1.2, letterSpacing: '-0.01em' },
      h4: { fontWeight: 700, fontSize: '1.3rem', lineHeight: 1.25 },
      h5: { fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.3 },
      h6: { fontWeight: 700, fontSize: '1rem', lineHeight: 1.3 },
      subtitle1: { fontSize: '1.15rem', fontWeight: 500, lineHeight: 1.6 },
      subtitle2: { fontSize: '1rem', fontWeight: 500 },
      body1: { fontSize: '1rem', lineHeight: 1.65 },
      body2: { fontSize: '0.9rem', lineHeight: 1.6 },
      button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.01em' },
      overline: { fontWeight: 700, letterSpacing: '0.12em' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          'html': { scrollBehavior: 'smooth' },
          '*::-webkit-scrollbar': { width: 10, height: 10 },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: isDark ? 'rgba(224,168,46,0.35)' : 'rgba(16,21,29,0.25)',
            borderRadius: 8,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 10,
            padding: '0.65rem 1.5rem',
            fontWeight: 600,
            transition: 'transform 0.15s ease, box-shadow 0.2s ease, background-color 0.2s ease',
            '&:hover': { transform: 'translateY(-2px)' },
          },
          containedPrimary: {
            color: '#15110A',
            boxShadow: '0 8px 20px rgba(224,168,46,0.25)',
            '&:hover': { boxShadow: '0 12px 28px rgba(224,168,46,0.38)' },
          },
          containedSecondary: {
            color: '#FFFFFF',
            boxShadow: '0 8px 20px rgba(34,160,107,0.25)',
            '&:hover': { boxShadow: '0 12px 28px rgba(34,160,107,0.38)' },
          },
          outlined: { borderWidth: '1.5px', '&:hover': { borderWidth: '1.5px' } },
          sizeLarge: { padding: '0.85rem 2rem', fontSize: '1rem' },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            backgroundImage: 'none',
            border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(16,21,29,0.06)',
            boxShadow: isDark
              ? '0 10px 30px rgba(0,0,0,0.35)'
              : '0 10px 30px rgba(16,21,29,0.06)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: 8 },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: { paddingLeft: '1.5rem', paddingRight: '1.5rem' },
        },
      },
    },
  };
};
